import {
  ArrowRight,
  Award,
  Clock,
  HeadphonesIcon,
  Puzzle,
  Rocket,
  Users,
  Zap,
} from 'lucide-react';
import { motion } from 'motion/react';

import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';

const services = [
  {
    id: 'implementation',
    name: 'Implementation',
    title: 'Professional Implementation',
    description: 'Get up and running fast with our expert-led deployment services. We handle the technical complexity so you can focus on results.',
    icon: Rocket,
    color: 'from-blue-500 to-blue-600',
    features: ['Rapid go-live', 'Zero-downtime migration', 'Custom configuration', 'Team training'],
    timeline: '2-4 weeks',
    href: '/services/implementation',
  },
  {
    id: 'advisory',
    name: 'Advisory Services',
    title: 'vCISO & Strategic Consulting',
    description: 'Executive-level security leadership without the full-time hire. Our vCISOs and advisors set strategy, run the program, and own board, customer, and auditor reporting.',
    icon: Users,
    color: 'from-purple-500 to-purple-600',
    features: ['vCISO leadership', 'Compliance roadmap', 'Risk strategy', 'Board & auditor reporting'],
    timeline: 'Ongoing',
    href: '/services/advisory',
  },
  {
    id: 'integration',
    name: 'Custom Integration',
    title: 'Seamless System Integration',
    description: 'Connect BOSSS with your existing tools and workflows. Our integration specialists ensure smooth data flow and unified operations.',
    icon: Puzzle,
    color: 'from-green-500 to-green-600',
    features: ['API development', 'Data mapping', 'Workflow automation', 'Testing & validation'],
    timeline: '1-3 weeks',
    href: '/services/integration',
  },
  {
    id: 'support',
    name: 'Dedicated Support',
    title: 'Enterprise Support & Success',
    description: 'Expert technical support with dedicated customer success managers to ensure you maximize your BOSSS investment.',
    icon: HeadphonesIcon,
    color: 'from-orange-500 to-orange-600',
    features: ['Dedicated helpdesk', 'Dedicated CSM', 'Priority escalation', 'Health monitoring'],
    timeline: 'Business hours',
    href: '/services/support',
  },
];

export function ServicesSection({ onNavigate, onNavigateToTop}) {
  return (
    <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-20"
          transition={{ duration: 0.8 }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="inline-flex items-center bg-purple-100 text-purple-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Award className="w-4 h-4 mr-2" />
            Professional Services
          </div>

          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Expert Support Every Step of the Way
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From initial deployment to ongoing optimization, our team of cybersecurity experts
            ensures your BOSSS implementation delivers maximum value and protection.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.id}
                className="group"
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <Card className="h-full border-gray-200 hover:border-blue-300 hover:shadow-xl transition-all duration-500 overflow-hidden relative">
                  {/* Animated Background */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />

                  <motion.div
                    whileHover={{ y: -5 }}
                    className="relative h-full"
                    transition={{ duration: 0.3 }}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between mb-4">
                        <motion.div
                          whileHover={{ rotate: 10 }}
                          className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}
                        >
                          <IconComponent className="w-8 h-8 text-white" />
                        </motion.div>

                        <div className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                          <Clock className="w-3 h-3 mr-1" />
                          {service.timeline}
                        </div>
                      </div>

                      <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">
                        {service.title}
                      </CardTitle>

                      <CardDescription className="text-gray-600 leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent>
                      {/* Features Grid */}
                      <div className="grid grid-cols-2 gap-3 mb-6">
                        {service.features.map((feature, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -10 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: idx * 0.1 }}
                            className="flex items-center text-sm text-gray-600 bg-white rounded-lg px-3 py-2 shadow-sm"
                          >
                            <Zap className="w-3 h-3 text-blue-500 mr-2 flex-shrink-0" />
                            <span>{feature}</span>
                          </motion.div>
                        ))}
                      </div>

                      <Button
                        className="w-full bg-white border border-gray-300 text-gray-700 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600 transition-all duration-300 hover:scale-105"
                        onClick={() => onNavigate(service.href)}
                      >
                        Learn More About
                        {' '}
                        {service.name}

                        <motion.div
                          whileHover={{ x: 5 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </motion.div>
                      </Button>
                    </CardContent>
                  </motion.div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center"
          viewport={{ once: true }}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="bg-white rounded-2xl p-12 border border-gray-200 shadow-lg max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h3>

            <p className="text-gray-600 mb-8">
              Our professional services team is ready to help you implement and optimize BOSSS
              for your specific needs. Contact us to discuss your requirements.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule Consultation
              </Button>

              <Button
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-lg transition-all hover:scale-105"
                onClick={() => onNavigateToTop('/contact')}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
